import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileSignature, Send, ExternalLink, Plus, Trash2, Sparkles, ShieldCheck, ShieldAlert, ScrollText } from "lucide-react";

const FIELD_LABELS = {
  DOCUSIGN_INTEGRATION_KEY: "Integration Key (Client ID)",
  DOCUSIGN_ACCOUNT_ID: "Account ID",
  DOCUSIGN_USER_ID: "Impersonated User ID",
  DOCUSIGN_PRIVATE_KEY: "RSA Private Key (JWT)",
};

const FIELD_HELP = {
  DOCUSIGN_INTEGRATION_KEY: "From developer.docusign.com → Apps and Keys",
  DOCUSIGN_ACCOUNT_ID: "Top-right of your DocuSign admin, or from /oauth/userinfo",
  DOCUSIGN_USER_ID: "Impersonated user (yours or a service account) — Apps and Keys → App details",
  DOCUSIGN_PRIVATE_KEY: "Paste the entire PEM block (BEGIN/END RSA PRIVATE KEY). Values stay server-side.",
};

export function DocuSignConfigCard({ onConfigured, autoOpenLegalPack = false }) {
  const [status, setStatus] = useState(null);
  const [counsel, setCounsel] = useState(null);
  const [form, setForm] = useState({
    DOCUSIGN_INTEGRATION_KEY: "",
    DOCUSIGN_ACCOUNT_ID: "",
    DOCUSIGN_USER_ID: "",
    DOCUSIGN_PRIVATE_KEY: "",
    DOCUSIGN_ENVIRONMENT: "demo",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/docusign/status"); setStatus(data); }
    catch { setStatus(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll counsel-status every 60s so the "Sent to counsel" pill flips to
  // "Counsel-signed" without a page reload. Initial fetch + then interval.
  useEffect(() => {
    let mounted = true;
    const fetchCounsel = async () => {
      try {
        const { data } = await api.get("/legal/counsel-status");
        if (mounted) setCounsel(data);
      } catch (err) { if (typeof console !== 'undefined') console.debug('components/DocuSignEnvelope.jsx: swallowed', err); }
    };
    fetchCounsel();
    const iv = setInterval(fetchCounsel, 60000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  const save = async () => {
    // Only send non-empty fields — server treats blank as "no change".
    const upd = Object.fromEntries(Object.entries(form).filter(([, v]) => v && v.trim()));
    if (Object.keys(upd).length === 0) return toast.error("Fill at least one field");
    setSaving(true);
    try {
      await api.post("/docusign/settings", upd);
      toast.success("DocuSign settings saved");
      load();
      onConfigured?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };

  if (!status) return null;
  return (
    <Card className="border-white/10 bg-card p-5" data-testid="docusign-config">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline flex items-center gap-1">
            <FileSignature className="h-3 w-3" /> DocuSign eSignature
          </div>
          <div className="text-lg font-semibold font-display mt-1">Legally-binding board packet signatures</div>
        </div>
        {status.configured ? (
          <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10">
            <ShieldCheck className="h-3 w-3 mr-1" /> Configured · {status.environment}
          </Badge>
        ) : (
          <Badge variant="outline" className="border-amber-500/40 text-amber-300 bg-amber-500/10">
            <ShieldAlert className="h-3 w-3 mr-1" /> Not configured
          </Badge>
        )}
      </div>
      <div className="text-sm text-zinc-400 mt-2">
        Get your credentials at <a className="underline underline-offset-2" href="https://developer.docusign.com" target="_blank" rel="noreferrer">developer.docusign.com</a> → Apps and Keys.
        Values stay server-side; the private key is never sent to the browser after save.
      </div>
      {status.missing?.length > 0 && (
        <div className="text-[11px] text-amber-300 mt-2">
          Missing: {status.missing.map((k) => FIELD_LABELS[k] || k).join(", ")}
        </div>
      )}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.keys(FIELD_LABELS).filter((k) => k !== "DOCUSIGN_PRIVATE_KEY").map((k) => (
          <div key={k}>
            <Label className="text-xs">{FIELD_LABELS[k]}</Label>
            <Input
              data-testid={`docusign-${k}`}
              value={form[k] || ""}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              placeholder={FIELD_HELP[k]}
              type={k === "DOCUSIGN_INTEGRATION_KEY" ? "text" : "text"}
            />
          </div>
        ))}
        <div>
          <Label className="text-xs">Environment</Label>
          <Select value={form.DOCUSIGN_ENVIRONMENT} onValueChange={(v) => setForm({ ...form, DOCUSIGN_ENVIRONMENT: v })}>
            <SelectTrigger data-testid="docusign-env" className="bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="demo">Demo / Sandbox (free)</SelectItem>
              <SelectItem value="production">Production (live)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-xs">{FIELD_LABELS.DOCUSIGN_PRIVATE_KEY}</Label>
        <Textarea
          data-testid="docusign-DOCUSIGN_PRIVATE_KEY"
          rows={5}
          value={form.DOCUSIGN_PRIVATE_KEY}
          onChange={(e) => setForm({ ...form, DOCUSIGN_PRIVATE_KEY: e.target.value })}
          placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;… paste full block …&#10;-----END RSA PRIVATE KEY-----"
          className="font-mono text-[11px]"
        />
        <div className="text-[10px] text-zinc-500 mt-1">Stored server-side in db.settings; masked in audit log.</div>
      </div>
      <Button data-testid="docusign-save" onClick={save} disabled={saving} className="mt-4 bg-orange-500 hover:bg-orange-600">
        {saving ? "Saving…" : "Save DocuSign settings"}
      </Button>
      {status?.configured && (
        <div className="mt-5 pt-5 border-t border-white/10">
          <div className="overline mb-2 flex items-center gap-1">
            <ScrollText className="h-3 w-3" /> Legal pack sign-off
          </div>
          <div className="text-sm text-zinc-400 mb-3">
            Send the Terms of Service, Privacy Policy, and Data Processing
            Addendum to your counsel as a single DocuSign envelope for red-line
            and sign-off. Signature + date tabs are placed on each PDF.
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <SendLegalPackToCounselDialog
              defaultOpen={autoOpenLegalPack}
              trigger={
                <Button data-testid="send-legal-pack-btn" variant="outline" className="border-white/10">
                  <ScrollText className="h-4 w-4 mr-2" /> Send legal pack to counsel
                </Button>
              }
            />
            <CounselStatusPill counsel={counsel} />
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Live pill that reflects the latest DocuSign legal-pack envelope status.
 * Auto-refreshes every 60s via DocuSignConfigCard's polling. Renders:
 *   - nothing when no envelope has been sent yet
 *   - amber "Sent — awaiting counsel" while envelope is in flight
 *   - emerald "Counsel-signed" once complete
 */
function CounselStatusPill({ counsel }) {
  if (!counsel || !counsel.envelope_id) {
    return (
      <span className="text-[11px] uppercase tracking-widest text-zinc-500" data-testid="counsel-pill-empty">
        No envelope sent yet
      </span>
    );
  }
  const sentDate = counsel.sent_at ? new Date(counsel.sent_at).toLocaleDateString() : "";
  if (counsel.signed) {
    return (
      <span
        data-testid="counsel-pill-signed"
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[11px] uppercase tracking-widest"
        title={`Envelope ${counsel.envelope_id?.slice(0, 8)}… signed by ${counsel.signer_count} counsel reviewer${counsel.signer_count === 1 ? "" : "s"}`}
      >
        <ShieldCheck className="h-3.5 w-3.5" /> Counsel-signed
        {sentDate && (
          <span className="text-emerald-400/70 normal-case tracking-normal text-[10px]">· {sentDate}</span>
        )}
      </span>
    );
  }
  return (
    <span
      data-testid="counsel-pill-sent"
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 border border-amber-500/40 bg-amber-500/10 text-amber-200 text-[11px] uppercase tracking-widest"
      title={`Envelope ${counsel.envelope_id?.slice(0, 8)}… — status ${counsel.status || "sent"}`}
    >
      <Sparkles className="h-3.5 w-3.5" /> Sent — awaiting counsel
      {sentDate && (
        <span className="text-amber-300/70 normal-case tracking-normal text-[10px]">· {sentDate}</span>
      )}
    </span>
  );
}

/**
 * Send the 3 legal PDFs (Terms, Privacy, DPA) to counsel for review + sign
 * in a single DocuSign envelope. Handles 501 (unconfigured) gracefully.
 * `defaultOpen` — when true (deep-link from palette), auto-opens on mount.
 */
export function SendLegalPackToCounselDialog({ trigger, defaultOpen = false }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [signers, setSigners] = useState([{ name: "", email: "", role: "Counsel" }]);
  const [subject, setSubject] = useState("Please review Prestigious legal pack (Terms, Privacy, DPA)");
  const [message, setMessage] = useState(
    "Attached are Prestigious HOA Portal's Terms of Service, Privacy Policy, and Data Processing Addendum for your review and red-line. Please sign each once you're satisfied — your signature indicates counsel-review sign-off, not customer acceptance.",
  );
  const [sending, setSending] = useState(false);

  // React to defaultOpen changes so the palette's ?legal_pack=1 deep-link
  // opens the dialog after mount, and closing the dialog stays closed.
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try { const { data } = await api.get("/docusign/status"); setStatus(data); }
      catch { setStatus(null); }
    })();
  }, [open]);

  const updateSigner = (idx, k, v) => setSigners((prev) => prev.map((s, i) => i === idx ? { ...s, [k]: v } : s));
  const addSigner = () => signers.length < 5 && setSigners((prev) => [...prev, { name: "", email: "", role: "Counsel" }]);
  const removeSigner = (idx) => setSigners((prev) => prev.filter((_, i) => i !== idx));

  const send = async () => {
    const cleaned = signers.filter((s) => s.name.trim() && s.email.trim());
    if (cleaned.length === 0) return toast.error("Add at least one signer with a name + email");
    setSending(true);
    try {
      const { data } = await api.post("/docusign/legal-envelope", {
        subject, message, signers: cleaned,
      });
      toast.success(`Legal pack ${data.envelope_id.slice(0, 8)}… sent to ${cleaned.length} counsel signer${cleaned.length === 1 ? "" : "s"}`);
      setOpen(false);
    } catch (e) {
      if (e?.response?.status === 501) {
        toast.error("DocuSign not configured — add your keys above first.");
      } else {
        toast.error(apiErrorMessage(e));
      }
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="legal-pack-dialog">
        <DialogHeader>
          <DialogTitle><ScrollText className="h-4 w-4 inline mr-1" /> Send legal pack to counsel</DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            One DocuSign envelope containing Terms, Privacy, and DPA — up to 5 counsel reviewers, sequential routing.
          </DialogDescription>
        </DialogHeader>
        {status && !status.configured && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
            <div className="flex items-center gap-2 font-medium mb-1">
              <ShieldAlert className="h-4 w-4" /> DocuSign not configured
            </div>
            Save your DocuSign credentials first — the envelope send will fail until keys are set.
          </div>
        )}
        {status?.configured && (
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/[0.05] p-2 text-[11px] text-emerald-200 flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Will send Terms v1.0, Privacy v1.0, DPA v1.1 via DocuSign {status.environment}.
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label>Subject</Label>
            <Input data-testid="lp-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea data-testid="lp-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 block">Counsel reviewers ({signers.length}/5)</Label>
            <div className="space-y-2 max-h-60 overflow-y-auto scroll-slim pr-1">
              {signers.map((s, idx) => (
                <div key={idx} className="rounded-md border border-white/10 bg-white/[0.02] p-2" data-testid={`lp-signer-${idx}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Reviewer {idx + 1}</div>
                    {signers.length > 1 && (
                      <button className="text-rose-300 hover:text-rose-200" onClick={() => removeSigner(idx)} data-testid={`lp-signer-rm-${idx}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input data-testid={`lp-name-${idx}`} placeholder="Name" value={s.name} onChange={(e) => updateSigner(idx, "name", e.target.value)} />
                    <Input data-testid={`lp-email-${idx}`} placeholder="counsel@firm.example" value={s.email} onChange={(e) => updateSigner(idx, "email", e.target.value)} />
                    <Input data-testid={`lp-role-${idx}`} placeholder="Role" value={s.role} onChange={(e) => updateSigner(idx, "role", e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
            {signers.length < 5 && (
              <Button data-testid="lp-add-signer" size="sm" variant="outline" className="border-white/10 mt-2" onClick={addSigner}>
                <Plus className="h-3 w-3 mr-1" /> Add reviewer
              </Button>
            )}
          </div>
          <Button data-testid="lp-send" onClick={send} disabled={sending} className="w-full bg-orange-500 hover:bg-orange-600">
            <Send className="h-4 w-4 mr-1" /> {sending ? "Sending…" : "Send legal pack via DocuSign"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * "Send via DocuSign" button — small dialog that adds signers by email, then
 * POSTs /api/docusign/envelopes. Handles 501 (unconfigured) gracefully by
 * pointing the admin to Settings.
 */
export function SendViaDocuSignDialog({ trigger, propertyId, defaultSigners = [] }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [signers, setSigners] = useState(
    defaultSigners.length > 0
      ? defaultSigners
      : [{ name: "", email: "", role: "Board Chair" }],
  );
  const [subject, setSubject] = useState("Please sign the board packet");
  const [message, setMessage] = useState("Please review and sign the attached monthly board packet.");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try { const { data } = await api.get("/docusign/status"); setStatus(data); }
      catch { setStatus(null); }
    })();
  }, [open]);

  const updateSigner = (idx, k, v) => setSigners((prev) => prev.map((s, i) => i === idx ? { ...s, [k]: v } : s));
  const addSigner = () => signers.length < 5 && setSigners((prev) => [...prev, { name: "", email: "", role: "Board Member" }]);
  const removeSigner = (idx) => setSigners((prev) => prev.filter((_, i) => i !== idx));

  const send = async () => {
    const cleaned = signers.filter((s) => s.name.trim() && s.email.trim());
    if (cleaned.length === 0) return toast.error("Add at least one signer with a name + email");
    setSending(true);
    try {
      const { data } = await api.post("/docusign/envelopes", {
        property_id: propertyId, subject, message, signers: cleaned,
      });
      toast.success(`Envelope ${data.envelope_id.slice(0, 8)}… sent to ${cleaned.length} signer${cleaned.length === 1 ? "" : "s"}`);
      setOpen(false);
    } catch (e) {
      // 501 = not configured — surface a helpful CTA
      if (e?.response?.status === 501) {
        toast.error("DocuSign not configured — open Admin → Settings to add your keys.");
      } else {
        toast.error(apiErrorMessage(e));
      }
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle><FileSignature className="h-4 w-4 inline mr-1" /> Send via DocuSign</DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            Send the current board packet PDF to up to 5 signers via a legally-binding DocuSign envelope.
          </DialogDescription>
        </DialogHeader>
        {status && !status.configured && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
            <div className="flex items-center gap-2 font-medium mb-1">
              <ShieldAlert className="h-4 w-4" /> DocuSign not configured
            </div>
            Add your DocuSign credentials in <a href="/admin/settings" className="underline">Admin → Settings</a> to enable legally-binding envelopes.
            You can still draft envelopes below — sending will fail until keys are set.
          </div>
        )}
        {status?.configured && (
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/[0.05] p-2 text-[11px] text-emerald-200 flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Envelope will be created via DocuSign {status.environment} — emails go out immediately.
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label>Subject</Label>
            <Input data-testid="ds-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea data-testid="ds-message" rows={2} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 block">Signers ({signers.length}/5)</Label>
            <div className="space-y-2 max-h-60 overflow-y-auto scroll-slim pr-1">
              {signers.map((s, idx) => (
                <div key={idx} className="rounded-md border border-white/10 bg-white/[0.02] p-2" data-testid={`ds-signer-${idx}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Signer {idx + 1}</div>
                    {signers.length > 1 && (
                      <button className="text-rose-300 hover:text-rose-200" onClick={() => removeSigner(idx)} data-testid={`ds-signer-rm-${idx}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input data-testid={`ds-name-${idx}`} placeholder="Name" value={s.name} onChange={(e) => updateSigner(idx, "name", e.target.value)} />
                    <Input data-testid={`ds-email-${idx}`} placeholder="email@example.com" value={s.email} onChange={(e) => updateSigner(idx, "email", e.target.value)} />
                    <Input data-testid={`ds-role-${idx}`} placeholder="Role" value={s.role} onChange={(e) => updateSigner(idx, "role", e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
            {signers.length < 5 && (
              <Button data-testid="ds-add-signer" size="sm" variant="outline" className="border-white/10 mt-2" onClick={addSigner}>
                <Plus className="h-3 w-3 mr-1" /> Add signer
              </Button>
            )}
          </div>
          <Button data-testid="ds-send" onClick={send} disabled={sending} className="w-full bg-orange-500 hover:bg-orange-600">
            <Send className="h-4 w-4 mr-1" /> {sending ? "Sending…" : "Send envelope"}
          </Button>
        </div>
        {status?.configured && (
          <a href="https://apps.docusign.com" target="_blank" rel="noreferrer" className="text-[11px] text-zinc-500 hover:text-white flex items-center gap-1 justify-center mt-1">
            <ExternalLink className="h-3 w-3" /> Track envelopes in DocuSign
          </a>
        )}
      </DialogContent>
    </Dialog>
  );
}
